import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import React, { useState, useEffect, useRef } from "react";
import { convertEpochToDateTime } from "../../../../helper/DateConverters";
import { defaultFormValues } from "./defaultFormValues";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { Toast } from "primereact/toast";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import axios from "../../../../util/axiosconfig";
import { useNavigate } from "react-router-dom";

const AddEquipmentform = ({
  formValues,
  setFormValues,
  isUploadInProgress,
  isEquipmentFormAttached = false,
}: any) => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const isDisabled = formSubmitting || isUploadInProgress;

  const [localFormValues, setLocalFormValues] = useState(defaultFormValues);

  // Determine if we're in standalone mode (no formValues prop provided)
  const isStandaloneMode = formValues === undefined || formValues === null;

  // Use local state in standalone mode, props in child mode
  const currentFormValues = isStandaloneMode ? localFormValues : formValues;

  const currentSetFormValues = isStandaloneMode
    ? setLocalFormValues
    : setFormValues;

  // Initialize formValues if parent passes null/undefined but provides setFormValues
  useEffect(() => {
    if (
      !isStandaloneMode &&
      !formValues &&
      typeof setFormValues === "function"
    ) {
      setFormValues(defaultFormValues);
    }
  }, [formValues, setFormValues, isStandaloneMode]);

  const safeSetFormValues = (updater: any) => {
    if (typeof currentSetFormValues === "function") {
      currentSetFormValues(updater);
    }
  };

  const validateForm = () => {
    if (!currentFormValues?.site || currentFormValues?.site.trim() === "") {
      showError("Site value is required", toast);
      return false;
    }
    if (
      !currentFormValues?.completedBy?.userID ||
      !currentFormValues?.completedBy?.displayName
    ) {
      showError("Completed By is required", toast);
      return false;
    }
    if (!currentFormValues?.dateTime) {
      showError("Date and Time is required", toast);
      return false;
    }

    const loweringEquipmentUnselected =
      currentFormValues?.loweringEquipment?.some((item: any) => !item.value);
    if (loweringEquipmentUnselected) {
      showError(
        "Please select Yes/No/N/A for all Lowering Equipment items.",
        toast
      );
      return false;
    }

    const surveyEquipmentUnselected = currentFormValues?.surveyEquipment?.some(
      (item: any) => !item.value
    );
    if (surveyEquipmentUnselected) {
      showError(
        "Please select Yes/No/N/A for all Survey Equipment items.",
        toast
      );
      return false;
    }

    return true; // Return true if valid, false otherwise
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      setFormSubmitting(true);
      const formData = new FormData();

      formData.append("formJson", JSON.stringify(currentFormValues));

      formData.append("isWeb", "true");

      const response = await axios.post(
        "health-and-safety/pre-use-equipment-check-list-form",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setFormSubmitting(false);
      showSuccess(response.data.Message, toast);
      navigate("/layout/health-and-safety/pre-use-equipment-checklist");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setFormSubmitting(false);
      showError(errorMessage, toast);
    }
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="form-border">
        {isStandaloneMode && (
          <div className="tw-bg-[#F8FAFC] tw-p-4 tw-rounded-t-lg tw-border-b-[1px] tw-border-[#E2E8F0] tw-flex tw-justify-between tw-items-center">
            <h2 className="tw-text-lg sm:tw-text-xl xl:tw-text-2xl tw-font-semibold tw-text-[#101828]">
              Pre-Use Equipment Checklist
            </h2>
          </div>
        )}
        <div>
          <div className="tw-p-2 sm:tw-p-4">
            <div className="tw-grid tw-grid-cols-1  2xl:tw-grid-cols-3 tw-gap-6 tw-w-full tw-mt-4">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                <label className="leak-label" htmlFor="teamMember1">
                  Site
                </label>
                <InputText
                  value={currentFormValues?.site || ""}
                  onChange={(e) =>
                    safeSetFormValues((prev: any) => ({
                      ...prev,
                      site: e.target.value,
                    }))
                  }
                  disabled={formSubmitting}
                  readOnly={isEquipmentFormAttached}
                  type="text"
                  className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-[#667085] tw-h-11 tw-w-full"
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                <label className="leak-label" htmlFor="teamMember1">
                  Completed By
                </label>
                <InputText
                  value={localStorage.displayName ?? ""}
                  readOnly
                  type="text"
                  className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-[#667085] tw-h-11 tw-w-full"
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                <label className="leak-label" htmlFor="teamMember1">
                  Date & Time
                </label>
                <InputText
                  readOnly
                  value={
                    convertEpochToDateTime(currentFormValues?.dateTime) || ""
                  }
                  type="text"
                  className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-[#667085] tw-h-11 tw-w-full"
                />
              </div>
            </div>
            <div>
              <DataTable
                value={currentFormValues?.loweringEquipment || []}
                className="tw-mt-4"
                scrollable
                showGridlines
              >
                <Column
                  field="label"
                  header="Lowering Equipment  "
                  style={{ maxWidth: "500px" }}
                />
                <Column
                  field="yes"
                  header="Yes"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.loweringEquipment[rowIndex].value ===
                        "yes"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`yes-${rowIndex}`}
                      inputId={`yes-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          loweringEquipment: prev.loweringEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "yes" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
                <Column
                  field="no"
                  header="No"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.loweringEquipment[rowIndex].value ===
                        "no"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`no-${rowIndex}`}
                      inputId={`no-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          loweringEquipment: prev.loweringEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "no" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
                <Column
                  field="n_a"
                  header="N/A"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.loweringEquipment[rowIndex].value ===
                        "n_a"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`n_a-${rowIndex}`}
                      inputId={`n_a-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          loweringEquipment: prev.loweringEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "n_a" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
              </DataTable>
              <DataTable
                value={currentFormValues?.surveyEquipment || []}
                className="tw-mt-4"
                scrollable
                showGridlines
              >
                <Column
                  field="label"
                  header="Survey Equipment  "
                  style={{ maxWidth: "500px" }}
                />
                <Column
                  field="yes"
                  header="Yes"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.surveyEquipment[rowIndex].value ===
                        "yes"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`yes-survey-${rowIndex}`}
                      inputId={`yes-survey-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          surveyEquipment: prev.surveyEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "yes" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
                <Column
                  field="no"
                  header="No"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.surveyEquipment[rowIndex].value ===
                        "no"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`no-survey-${rowIndex}`}
                      inputId={`no-survey-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          surveyEquipment: prev.surveyEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "no" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
                <Column
                  field="n_a"
                  header="N/A"
                  style={{ maxWidth: "100px" }}
                  body={(rowData, { rowIndex }) => (
                    <RadioButton
                      checked={
                        currentFormValues?.surveyEquipment[rowIndex].value ===
                        "n_a"
                      }
                      disabled={isEquipmentFormAttached || formSubmitting}
                      name={`n_a-survey-${rowIndex}`}
                      inputId={`n_a-survey-${rowIndex}`}
                      className="tw-mr-2"
                      onChange={() =>
                        safeSetFormValues((prev: any) => ({
                          ...prev,
                          surveyEquipment: prev.surveyEquipment.map(
                            (item: any, index: number) =>
                              index === rowIndex
                                ? { ...item, value: "n_a" }
                                : item
                          ),
                        }))
                      }
                    />
                  )}
                />
              </DataTable>
            </div>
            <div className="tw-mt-10 tw-flex tw-justify-center tw-items-center tw-gap-2 tw-flex-col  ">
              <span className="tw-underline tw-font-bold tw-text-xl">
                DO NOT USE{" "}
              </span>
              <p className="tw-text-center text-sm tw-font-semibold">
                Any equipment that is faulty or you are unsure if it is safe for
                use. Only trained competent persons are permitted to use all
                CCTV Equipment
              </p>
            </div>
          </div>
          {isStandaloneMode && (
            <div className="tw-flex tw-justify-end tw-items-center tw-p-4  tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
              <Button
                className="form-cancel-button"
                label="Cancel"
                disabled={isDisabled}
                onClick={() =>
                  navigate(
                    "/layout/health-and-safety/pre-use-equipment-checklist"
                  )
                }
                color="white"
              />
              <Button
                className="tw-flex tw-px-3 focus:tw-shadow-none tw-py-2 tw-rounded tw-bg-primary tw-border-primary tw-text-white tw-font-medium"
                label={`${formSubmitting ? "Saving..." : "Save"}`}
                loading={formSubmitting}
                disabled={isDisabled}
                onClick={handleSave}
              />
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AddEquipmentform;
